angular.module('MyHippoProducer.Services').factory('AmplitudeService', function () {
    const { AMPLITUDE_KEY } = window.appConfig || {};
    const amplitudeApiKey = AMPLITUDE_KEY;

    let isInitialized = false;
    let isScriptLoading = false;
    let pendingCalls = [];
    const MAX_PENDING_CALLS = 20;

    const AMPLITUDE_MASK = 'AMPLITUDE_MASK';
    const AMPLITUDE_UNMASK = 'AMPLITUDE_UNMASK';

    const getLocationHostName = () => window.location.hostname;
    const getDomainName = () => getLocationHostName().split('.').slice(-2).join('.');
    const getCookieDomain = () => '.' + getDomainName();

    const getAmplitudeConfig = () => ({
        fetchRemoteConfig: true,
        autocapture: {
            elementInteractions: true,
            formInteractions: false,
            pageViews: {
                trackHistoryChanges: 'all',
            },
            webVitals: true,
        },
        cookieOptions: {
            domain: getCookieDomain(),
            expiration: 30,
        },
        logLevel: 1,
        optOut: false,
        trackingOptions: {
            ipAddress: false,
            language: false,
        },
    });

    const initAmplitude = (enableAmplitudeAnalytics = true) => {
        if (!amplitudeApiKey || isInitialized || isScriptLoading || !enableAmplitudeAnalytics) {
            return;
        }

        isScriptLoading = true;

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://cdn.amplitude.com/script/${amplitudeApiKey}.js`;
        script.onload = () => {
            if (!window.amplitude) {
                isScriptLoading = false;
                pendingCalls = [];
                return;
            }

            if (window.sessionReplay) {
                window.amplitude.add(window.sessionReplay.plugin({
                    logLevel: 1,
                    optOut: false,
                    privacyConfig: {
                        defaultMaskLevel: 'medium',
                        maskSelector: [AMPLITUDE_MASK],
                        unmaskSelector: [AMPLITUDE_UNMASK],
                    },
                    sampleRate: 1,
                    shouldInlineStylesheet: true,
                }));
            }

            window.amplitude.init(amplitudeApiKey, getAmplitudeConfig());
            isInitialized = true;
            isScriptLoading = false;

            const queuedCalls = pendingCalls;
            pendingCalls = [];
            queuedCalls.forEach((callback) => callback(window.amplitude));
        };

        script.onerror = () => {
            isScriptLoading = false;
            pendingCalls = [];
        };

        const amplitudeScript = document.getElementsByTagName('script')[0];
        if (amplitudeScript && amplitudeScript.parentNode) {
            amplitudeScript.parentNode.insertBefore(script, amplitudeScript);
            return;
        }

        document.head.appendChild(script);
    };

    const getAmplitudeClient = () => {
        initAmplitude();
        return window.amplitude;
    };

    const runWhenInitialized = (callback) => {
        const amplitude = getAmplitudeClient();

        if (!amplitudeApiKey) {
            return;
        }

        if (amplitude && isInitialized) {
            callback(amplitude);
            return;
        }

        if (pendingCalls.length >= MAX_PENDING_CALLS) {
            pendingCalls.shift();
        }

        pendingCalls.push(callback);
    };

    const trackEvent = (eventName, properties) => {
        runWhenInitialized((amplitude) => {
            if (amplitude.track) {
                amplitude.track(eventName, properties);
            }
        });
    };

    const setUserId = (value) => {
        runWhenInitialized((amplitude) => {
            if (amplitude.setUserId) {
                amplitude.setUserId(value);
            }
        });
    };

    const identify = (properties) => {
        runWhenInitialized((amplitude) => {
            if (amplitude.identify && amplitude.Identify) {
                const identifyObj = new amplitude.Identify();
                Object.entries(properties || {}).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        identifyObj.set(key, value);
                    }
                });
                amplitude.identify(identifyObj);
                return;
            }

            if (amplitude.setUserProperties) {
                amplitude.setUserProperties(properties);
            }
        });
    };

    const addUserProperties = (properties) => {
        identify(properties);
    };

    const resetIdentity = () => {
        runWhenInitialized((amplitude) => {
            if (amplitude.reset) {
                amplitude.reset();
            }
        });
    };

    const setGroup = (groupType, groupName) => {
        runWhenInitialized((amplitude) => {
            if (amplitude.setGroup) {
                amplitude.setGroup(groupType, groupName);
            }
        });
    };

    const setOptOut = (optOut) => {
        runWhenInitialized((amplitude) => {
            if (amplitude.setOptOut) {
                amplitude.setOptOut(optOut);
            }
        });
    };

    const groupIdentify = (groupType, groupName, properties) => {
        runWhenInitialized((amplitude) => {
            if (amplitude.groupIdentify && amplitude.Identify) {
                const identifyObj = new amplitude.Identify();
                Object.entries(properties || {}).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        identifyObj.set(key, value);
                    }
                });

                amplitude.groupIdentify(groupType, groupName, identifyObj);
            }
        });
    };

    return {
        init: initAmplitude,
        trackEvent,
        setUserId,
        identify,
        addUserProperties,
        resetIdentity,
        setGroup,
        setOptOut,
        groupIdentify,
    };
});
